/**
 * Custom object definition spec — the DATA form of a sketch-object type.
 *
 * Organisations author custom library objects in the Object Editor; the
 * result is an {@link ObjectDefinitionSpec} stored on the server (validated
 * with `validateObjectDefinitionSpec`) and turned into a regular sketch-object
 * type on the client (`buildCustomSketchObjectType`), so rendering, handles,
 * the gizmo, the property panel and DXF export treat it like a built-in.
 *
 * Coordinate system: every coordinate is in METERS in the object's local
 * frame at its DEFAULT size — `u` along the width (`dir`), `n` along the
 * depth (`norm`), origin at the footprint center, so the default footprint
 * spans `[-width/2, width/2] × [-depth/2, depth/2]`. For wall objects −n is
 * the back (against the wall). Angles are radians in the u/n plane
 * (0 = +u, π/2 = +n).
 *
 * SCHEMA: param keys and the definition id are persisted in placed instances
 * (`sketchobject_c_<id>_<key>`); never rename them after publishing.
 */

export const OBJECT_DEFINITION_SCHEMA_VERSION = 1;

/** `[u, n]` in meters, authored frame. */
export type Vec2 = readonly [number, number];

/** `[x, y, z]`; for 3D models x = u, y = n, z = up. */
export type Vec3 = readonly [number, number, number];

export type ObjectDefinitionAnchor = 'POINT' | 'LINE';

export type ObjectDefinitionConstraint = { kind: 'FREE' } | { kind: 'WALL'; defaultInset?: number };

// ─── Params ─────────────────────────────────────────────────────────────

interface ObjectParamBase {
  /** Stable key: letters/digits, starts with a letter (SCHEMA). */
  key: string;
  label: string;
  /** Panel row order override (see ParamSpec.order in the client). */
  order?: number;
}

export interface ObjectFloatParam extends ObjectParamBase {
  kind: 'float';
  default: number;
  min?: number;
  max?: number;
  decimals?: number;
  /** Render with the user's meter/feet preference. */
  distance?: boolean;
}

export interface ObjectIntParam extends ObjectParamBase {
  kind: 'int';
  default: number;
  min?: number;
  max?: number;
}

export interface ObjectBooleanParam extends ObjectParamBase {
  kind: 'boolean';
  default: boolean;
}

export interface ObjectSelectParam extends ObjectParamBase {
  kind: 'select';
  default: string;
  options: ReadonlyArray<{ value: string; label: string }>;
}

export type ObjectParam = ObjectFloatParam | ObjectIntParam | ObjectBooleanParam | ObjectSelectParam;

export type ObjectParamValue = number | string | boolean;

/** A number that is constant, read from a numeric param, or a formula (see expression.ts). */
export type NumberBinding = { value: number } | { param: string } | { expr: string };

/**
 * A named value computed from the live size, params and earlier formulas —
 * e.g. `fw = max(0.1, min(flightWidth, width - 0.2))`. Formulas are
 * evaluated in order and usable wherever an expression is.
 */
export interface ObjectFormula {
  /** Same rules as a param key; unique among params and formulas. */
  key: string;
  expr: string;
  /** Short name saying what it IS ("Flight width", "Left leg handle") — shown in the editor's rules list. */
  label?: string;
  /** What it means, for the editor. */
  note?: string;
}

/** `param := expr` (evaluated with the current values, all assignments of a list at once). */
export interface ObjectParamAssignment {
  param: string;
  expr: string;
}

// ─── Shapes ─────────────────────────────────────────────────────────────

export type ObjectShapeStyle = 'solid' | 'dashed';

/**
 * `glyph` — always drawn; `selection` — only while the placed object is
 * selected (e.g. a beam's centerline).
 */
export type ObjectShapeLayer = 'glyph' | 'selection';

interface ObjectShapeBase {
  /** Unique within the spec. */
  id: string;
  name?: string;
  style?: ObjectShapeStyle;
  layer?: ObjectShapeLayer;
  /** Hidden in the editor AND at runtime (kept for later use). */
  hidden?: boolean;
  /**
   * Coordinates given by formulas instead of their fixed value, keyed by
   * field: polyline `p<index>.u` / `p<index>.n`; rect `min.u min.n max.u
   * max.n`; circle `center.u center.n radius`; arc the circle's plus
   * `startAngle endAngle` (radians). Formulas give the AUTHORED coordinate —
   * the group's resize rule still applies, so a group whose shapes are fully
   * formula-driven uses `fixed` (identity) and reads the live `width` /
   * `depth` itself. Inside an `index` repeat `i` and `count` are available.
   * Curves (`path`) take no formulas.
   */
  expr?: Readonly<Record<string, string>>;
}

export interface ObjectPolylineShape extends ObjectShapeBase {
  kind: 'polyline';
  points: ReadonlyArray<Vec2>;
  closed: boolean;
}

export interface ObjectRectShape extends ObjectShapeBase {
  kind: 'rect';
  min: Vec2;
  max: Vec2;
}

export interface ObjectCircleShape extends ObjectShapeBase {
  kind: 'circle';
  center: Vec2;
  radius: number;
}

export interface ObjectArcShape extends ObjectShapeBase {
  kind: 'arc';
  center: Vec2;
  radius: number;
  startAngle: number;
  /** May be smaller than startAngle (clockwise sweep). */
  endAngle: number;
}

/**
 * A bezier path anchor (pen tool). `in` / `out` are ABSOLUTE control points
 * (so every transform maps them like any other point); a missing one makes
 * that side a sharp corner. A node with both, collinear through `point`, is
 * a smooth node.
 */
export interface ObjectPathNode {
  point: Vec2;
  in?: Vec2;
  out?: Vec2;
}

/** Cubic bezier path: segment i runs node i → i+1 via `nodes[i].out` and `nodes[i+1].in`. */
export interface ObjectPathShape extends ObjectShapeBase {
  kind: 'path';
  nodes: ReadonlyArray<ObjectPathNode>;
  closed: boolean;
}

export type ObjectShape = ObjectPolylineShape | ObjectRectShape | ObjectCircleShape | ObjectArcShape | ObjectPathShape;

export type ObjectShapeKind = ObjectShape['kind'];

// ─── Groups: resize behaviour, repeats, conditional visibility ──────────

/**
 * How a group follows a footprint resize along one axis (9-slice style):
 *  - `stretch`  — coordinates scale with the footprint (default).
 *  - `fixed`    — keeps its size and stays centered.
 *  - `pinStart` — keeps its size and its distance to the −edge.
 *  - `pinEnd`   — keeps its size and its distance to the +edge.
 *  - `float`    — keeps its size; its center moves proportionally.
 */
export type ObjectAxisResize = 'stretch' | 'fixed' | 'pinStart' | 'pinEnd' | 'float';

/**
 * Keep a group's proportions: ONE scale factor for both axes (circles stay
 * circles), taken from the object's resize — the width's ratio, the depth's,
 * the smaller of the two (`fit`: always fits inside) or the larger (`fill`).
 * The group scales about the object's center; it overrides the per-axis rules.
 */
export type ObjectUniformScale = 'width' | 'depth' | 'fit' | 'fill';

export type ObjectRepeatSkip = 'none' | 'first' | 'last' | 'both';

/**
 * Repeat a group's shapes as cells along one axis (stair treads, cabinet
 * modules, closet doors). The group's shapes draw ONE cell spanning `cell`
 * along `axis`; copies fill the `run` span (default: the whole footprint),
 * which follows the group's resize rule for that axis.
 *
 * Cell count: `count` when only count is given, else `round(run / pitch)`.
 * When both are param-bound they are kept in sync like the stair's
 * step ↔ number of steps (the pitch is the authored invariant; editing the
 * count redistributes the run).
 */
export interface ObjectLinearRepeat {
  kind: 'linear';
  axis: 'u' | 'n';
  cell: readonly [number, number];
  run?: readonly [number, number];
  pitch?: NumberBinding;
  count?: NumberBinding;
  /** `fill` — cells stretch evenly to fill the run; `exact` — exact pitch, leftover split at both ends. */
  fit: 'fill' | 'exact';
  /** `stretch` — cell contents scale to the actual cell size; `fixed` — keep size, centered in the cell. */
  cellResize: 'stretch' | 'fixed';
  /** Drop the first/last cell (e.g. dividers BETWEEN modules). */
  skip?: ObjectRepeatSkip;
  /** Mirror every other cell along the axis (alternating door hinges). */
  mirrorAlternate?: boolean;
  minCount?: number;
  maxCount?: number;
  /**
   * Advanced: the live run as expressions (m, frame coordinates along
   * `axis`) instead of `run` + the resize rule — e.g. `-width / 2` to
   * `width / 2 - flightWidth`. See expression.ts.
   */
  runExpr?: { start: string; end: string };
}

/**
 * Repeat a group's shapes as rotated copies around `center` (spiral stair
 * treads). Copy i is rotated by `startAngle + i · sweep / count`,
 * i = 0…count (count + 1 copies before `skip`).
 */
export interface ObjectRadialRepeat {
  kind: 'radial';
  center: Vec2;
  count: NumberBinding;
  startAngle: number;
  sweep: number;
  skip?: ObjectRepeatSkip;
}

/**
 * Draw a group's shapes `count` times with no transform of its own: the
 * shapes' formulas (and the group offset) read `i` (0…count-1) and `count`
 * to place each copy — e.g. the treads of one flight of a two-flight stair,
 * or chairs spread around a round table.
 */
export interface ObjectIndexRepeat {
  kind: 'index';
  count: NumberBinding;
}

export type ObjectRepeat = ObjectLinearRepeat | ObjectRadialRepeat | ObjectIndexRepeat;

/**
 * Mirror modifier: the group is drawn a second time, mirrored across the
 * object's center — `u` left ↔ right, `n` back ↔ front, `both` gives the
 * other three quarters too. Applied to the LIVE drawing (after the resize
 * rules), so a part pinned to the left end gets a twin pinned to the right
 * end. Edit one side, the other follows.
 */
export interface ObjectMirrorModifier {
  kind: 'mirror';
  axis: 'u' | 'n' | 'both';
  /** Off: kept in the stack but not drawn. */
  enabled?: boolean;
}

/**
 * Round corners modifier: every corner of the group's lines and outlines is
 * rounded with a real-size radius (m) — after the resize rules, so it stays
 * round at any size. A radius that doesn't fit a corner shrinks to fit.
 * Circles, arcs and gentle bends of curves are left as they are.
 */
export interface ObjectRoundCornersModifier {
  kind: 'roundCorners';
  radius: NumberBinding;
  enabled?: boolean;
}

/** A non-destructive effect on a group's drawing (Blender-style), applied in stack order. */
export type ObjectModifier = ObjectMirrorModifier | ObjectRoundCornersModifier;

export type ObjectModifierKind = ObjectModifier['kind'];

export interface ObjectVisibilityCondition {
  param: string;
  equals: ObjectParamValue;
}

export interface ObjectGroup {
  id: string;
  name: string;
  /** Each shape belongs to at most one group; ungrouped shapes stretch. */
  shapeIds: ReadonlyArray<string>;
  resize: { u: ObjectAxisResize; n: ObjectAxisResize; uniform?: ObjectUniformScale };
  repeat?: ObjectRepeat;
  visibleWhen?: ObjectVisibilityCondition;
  /** Advanced: shown only while this formula is not 0 (on top of `visibleWhen`). */
  visibleIf?: string;
  /**
   * Advanced: place the group after its resize rule — turn it by `rotate`
   * (radians, about the origin) and shift it by `u` / `n` (m), all formulas.
   * With an `index` repeat each copy gets its own placement (via `i`).
   */
  offset?: { u?: string; n?: string; rotate?: string };
  /** Mirror / round corners, in order (see {@link ObjectModifier}). */
  modifiers?: ReadonlyArray<ObjectModifier>;
}

// ─── Handles ────────────────────────────────────────────────────────────

/**
 * A handle position: a `[u, n]` pair of FRACTIONS of the live footprint
 * (`[-0.5, 0.5]` per axis), or formulas giving the live position in meters.
 */
export type ObjectHandlePoint = Vec2 | { u: string; n: string };

/** Whether a handle position is given by formulas (not footprint fractions). */
export function isFormulaHandlePoint(p: ObjectHandlePoint): p is { u: string; n: string } {
  return !Array.isArray(p);
}

export interface ObjectDepthHandle {
  /** Short name saying what it IS ("Flight width", "Left leg handle") — shown in the editor's rules list. */
  label?: string;
  /** Which norm-side edge the handle drags (the other stays put). */
  side: 1 | -1;
  /** Position along the width as a fraction (on the dragged edge)… */
  u?: number;
  /** …or anywhere, by formulas (m, live frame). */
  at?: { u: string; n: string };
  /**
   * Custom drag: set params from `pulled` (the cursor's distance from the
   * edge that stays, m) instead of resizing directly. The depth then follows
   * the `depthLink` formula (or `pulled` without one).
   */
  assign?: ReadonlyArray<ObjectParamAssignment>;
}

/** Resize-handle placement of LINE objects (so handles follow resizes). */
export interface ObjectHandles {
  start?: ObjectHandlePoint;
  end?: ObjectHandlePoint;
  depth?: ReadonlyArray<ObjectDepthHandle>;
}

// ─── Coupled values on placed objects ───────────────────────────────────

/**
 * Keep a step size and a number of steps in line with a run length — the
 * stair's `step` ↔ `number of steps`:
 *  - count edited → step := run / count;
 *  - anything else changed → count := max(minCount, round(run / step)).
 * `exactStep` keeps the typed step when the count follows a resize (only a
 * direct count edit changes it); without it the step is evened out to
 * run / count as well.
 */
export interface ObjectStepSync {
  /** Short name saying what it IS ("Flight width", "Left leg handle") — shown in the editor's rules list. */
  label?: string;
  /** Float param: the step (tread depth, module width…). */
  step: string;
  /** Int param: the number of steps. */
  count: string;
  /** Formula: the run the steps divide. */
  run: string;
  minCount?: number;
  exactStep?: boolean;
}

/**
 * The depth follows a formula of the params (`flightWidth + max(leftLeg,
 * rightLeg)`); resizing the depth directly runs `onResize` instead, which
 * sets params from the new `depth` so the formula lands on it again.
 */
export interface ObjectDepthLink {
  /** Short name saying what it IS ("Flight width", "Left leg handle") — shown in the editor's rules list. */
  label?: string;
  expr: string;
  onResize?: ReadonlyArray<ObjectParamAssignment>;
}

// ─── 3D model ───────────────────────────────────────────────────────────

/**
 * How the model follows the live footprint per axis:
 *  - `stretch` — scales with the footprint (width/depth/height ratio).
 *  - `fixed`   — keeps its authored scale.
 *  - `uniform` — scales by the smallest stretched axis ratio (no distortion).
 */
export type ObjectModelScaleMode = 'stretch' | 'fixed' | 'uniform';

export interface ObjectModel3D {
  /** Asset id of a normalized GLB on the definition. */
  assetId: string;
  /** Where the model's origin (`pivot`) sits in the authored frame at default size (z = up from the floor). */
  position: Vec3;
  /** Euler XYZ radians. */
  rotation: Vec3;
  scale: Vec3;
  /**
   * The model's origin: the point of the model file (its own coordinates)
   * that `position` places, and that rotation and scale turn around.
   * Absent = the file's own origin. Moving it doesn't move the model.
   */
  pivot?: Vec3;
  scaleMode: { u: ObjectModelScaleMode; n: ObjectModelScaleMode; h: ObjectModelScaleMode };
}

// ─── Doors & windows ────────────────────────────────────────────────────

export type ObjectOpeningKind = 'DOOR' | 'WINDOW';

/**
 * Marks the spec as a door or window symbol instead of a placed object: it is
 * drawn INSIDE an opening on a wall (an OPENING_SKETCH), offered in the door /
 * window type dropdown and filed in the fixed Doors / Windows category.
 *
 * Frame: `u` along the wall, the opening spanning `[-width/2, width/2]`
 * around the origin, the HINGE side at −u; `n` across the wall, 0 on the
 * symbol's line (the wall center by default), +n the side the door opens to
 * (a window's outside). Placed openings mirror the drawing for the other
 * hinge / side. There is no footprint: the symbol scales with the opening
 * width — the live depth always equals the width × `depth / width` of the
 * default size, so `stretch` across the wall scales with the width like a
 * door swing does, `fixed` keeps a size (glass lines). Expressions can also
 * read `wall` (the wall thickness, m). On curved walls the drawing bends
 * along the wall.
 */
export interface ObjectOpening {
  kind: ObjectOpeningKind;
}

/** The fixed library categories of door / window definitions (SCHEMA). */
export const OPENING_CATEGORY_IDS: Readonly<Record<ObjectOpeningKind, string>> = { DOOR: 'DOORS', WINDOW: 'WINDOWS' };

/** The wall thickness variable of door / window expressions. */
export const OPENING_WALL_VARIABLE = 'wall';

/** Wall thickness assumed where no wall is known (editor drawing, library previews). */
export const DEFAULT_OPENING_WALL_THICKNESS = 0.2;

/** Which opening kind a category holds (null: an object category). */
export function openingKindOfCategory(categoryId: string): ObjectOpeningKind | null {
  if (categoryId === OPENING_CATEGORY_IDS.DOOR) return 'DOOR';
  if (categoryId === OPENING_CATEGORY_IDS.WINDOW) return 'WINDOW';
  return null;
}

// ─── Dimensions ─────────────────────────────────────────────────────────

/**
 * Marks the spec as a dimension annotation instead of a placed object: the
 * design tab's dimension tool draws it between two measured points and the
 * runtime labels it with the measured length (never an overridden text).
 *
 * Frame: `u` along the measured line, the two measured points at
 * `-width/2` and `+width/2`; `n` across it, 0 on the line. The live width is
 * the measured length, the depth stays at the default (the size of the
 * extension lines / end symbols), so end symbols are `fixed` across and
 * `pinStart` / `pinEnd` along the line. Params choose its look (end symbol,
 * line style, …); it has no handles, linked depth or 3D model: only its
 * endpoints move.
 *
 * Expressions also read `offset` (m, along +n): how far the user pulled the
 * dimension line off the measured points (the third click when placing; 0
 * in the editor drawing). A dimension shifts its line and end symbols by it
 * (group offset `n: offset`) and runs its extension lines from n = 0 to it.
 */
export interface ObjectDimension {
  kind: 'LINEAR';
}

/** The fixed library category of dimension definitions (SCHEMA). */
export const DIMENSION_CATEGORY_ID = 'DIMENSIONS';

/** The offset variable of dimension expressions (see {@link ObjectDimension}). */
export const DIMENSION_OFFSET_VARIABLE = 'offset';

/** The category a spec must be filed under when it is a door / window or a dimension (null: any object category). */
export function requiredCategoryOf(spec: Pick<ObjectDefinitionSpec, 'opening' | 'dimension'>): string | null {
  if (spec.opening) return OPENING_CATEGORY_IDS[spec.opening.kind];
  return spec.dimension ? DIMENSION_CATEGORY_ID : null;
}

/** Whether a category is one of the fixed class categories (doors, windows, dimensions) that only take their own class. */
export function isFixedClassCategory(categoryId: string): boolean {
  return openingKindOfCategory(categoryId) !== null || categoryId === DIMENSION_CATEGORY_ID;
}

// ─── Spec ───────────────────────────────────────────────────────────────

export interface ObjectDefinitionSize {
  width: number;
  depth: number;
  height: number;
}

export interface ObjectDefinitionSpec {
  schemaVersion: typeof OBJECT_DEFINITION_SCHEMA_VERSION;
  anchor: ObjectDefinitionAnchor;
  constraint: ObjectDefinitionConstraint;
  /** LINE + FREE only: draw as a two-point run anywhere (wall-editor style). */
  drawLikeWall?: boolean;
  defaultSize: ObjectDefinitionSize;
  params: ReadonlyArray<ObjectParam>;
  /** Computed values (see {@link ObjectFormula}). */
  formulas?: ReadonlyArray<ObjectFormula>;
  shapes: ReadonlyArray<ObjectShape>;
  groups: ReadonlyArray<ObjectGroup>;
  handles?: ObjectHandles;
  stepSyncs?: ReadonlyArray<ObjectStepSync>;
  depthLink?: ObjectDepthLink;
  model3d?: ObjectModel3D | null;
  /** A door / window symbol (see {@link ObjectOpening}); absent for placed objects. */
  opening?: ObjectOpening;
  /** A dimension annotation (see {@link ObjectDimension}); absent for placed objects. */
  dimension?: ObjectDimension;
}

/** Hard limits, enforced by the validator (and the evaluator for runtime counts). */
export const OBJECT_DEFINITION_LIMITS = {
  maxShapes: 2000,
  maxPoints: 20000,
  maxGroups: 200,
  maxParams: 24,
  maxFormulas: 64,
  maxRepeatCount: 500,
  maxModifiers: 8,
  maxEvaluatedSegments: 50000,
  maxSize: 100,
  minSize: 0.01,
  maxLabelLength: 80,
} as const;

export const OBJECT_PARAM_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9]{0,31}$/;

/** Prefix of custom sketch-object type ids (`c_<definitionId>`) — SCHEMA. */
export const CUSTOM_SKETCH_OBJECT_TYPE_PREFIX = 'c_';

export function customSketchObjectTypeId(definitionId: string): `c_${string}` {
  return `${CUSTOM_SKETCH_OBJECT_TYPE_PREFIX}${definitionId}`;
}

export function definitionIdFromCustomTypeId(typeId: string): string | null {
  return typeId.startsWith(CUSTOM_SKETCH_OBJECT_TYPE_PREFIX) ? typeId.slice(CUSTOM_SKETCH_OBJECT_TYPE_PREFIX.length) : null;
}

/** Type id of an organisation door / window (`c_<definitionId>`, same scheme as objects) — stored in the opening's door / window type. */
export type CustomOpeningTypeId = `c_${string}`;

export function isCustomOpeningTypeId(value: string): value is CustomOpeningTypeId {
  return value.startsWith(CUSTOM_SKETCH_OBJECT_TYPE_PREFIX);
}

/**
 * An empty spec: a free-standing 1 × 0.6 m object with nothing drawn yet.
 * (The editor shows the footprint as a guide; it is not part of the glyph.)
 */
export function createEmptyObjectDefinitionSpec(): ObjectDefinitionSpec {
  return {
    schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
    anchor: 'POINT',
    constraint: { kind: 'FREE' },
    defaultSize: { width: 1, depth: 0.6, height: 0.9 },
    params: [],
    shapes: [],
    groups: [],
    handles: undefined,
    model3d: null,
  };
}

/** Default size of a new dimension: a 2 m line, end symbols sized within 0.3 m across it. */
export const DEFAULT_DIMENSION_SIZE: ObjectDefinitionSize = { width: 2, depth: 0.3, height: 0.01 };

/**
 * A new dimension: just the measured line (stretching with the length), the
 * end symbols left to draw. Drawn like a wall: two clicks anywhere.
 */
export function createEmptyDimensionSpec(): ObjectDefinitionSpec {
  const half = DEFAULT_DIMENSION_SIZE.width / 2;
  return {
    schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
    anchor: 'LINE',
    constraint: { kind: 'FREE' },
    drawLikeWall: true,
    defaultSize: { ...DEFAULT_DIMENSION_SIZE },
    params: [],
    shapes: [
      {
        id: 'line',
        name: 'Dimension line',
        kind: 'polyline',
        points: [
          [-half, 0],
          [half, 0],
        ],
        closed: false,
      },
    ],
    groups: [{ id: 'line', name: 'Dimension line', shapeIds: ['line'], resize: { u: 'stretch', n: 'fixed' } }],
    model3d: null,
    dimension: { kind: 'LINEAR' },
  };
}

/**
 * An empty door / window: nothing drawn yet, at the default opening size
 * (door 0.9 × 2.1 m, window 1.2 × 0.6 m). Depth = width: the drawing across
 * the wall scales 1:1 with the opening width.
 */
export function createEmptyOpeningSpec(kind: ObjectOpeningKind): ObjectDefinitionSpec {
  const width = kind === 'DOOR' ? 0.9 : 1.2;
  return {
    schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
    anchor: 'LINE',
    constraint: { kind: 'WALL' },
    defaultSize: { width, depth: width, height: kind === 'DOOR' ? 2.1 : 0.6 },
    params: [],
    shapes: [],
    groups: [],
    model3d: null,
    opening: { kind },
  };
}
